import React, {FC, PropsWithChildren, useCallback, useMemo, useRef} from 'react';

import {useLayer} from '../components/layers/layerContext';
import {CssContextProvider} from './cssContext';

/*
 * State.
 */

interface CssState {
  readonly instances: number;
  readonly element: HTMLElement;
}

interface CssProviderState {
  [K: string]: CssState;
}

/*
 * Component.
 */

export const CssManager: FC<PropsWithChildren<unknown>> = ({children}) => {
  const {window} = useLayer();
  const stateRef = useRef<CssProviderState>({});

  /*
   * Context API.
   */

  const registerCss = useCallback(
    (urlToCssFile: string, onLoad?: () => void) => {
      const currentState = stateRef.current;

      // If we already have a state, just increment.
      const existingCssState = currentState[urlToCssFile];
      if (existingCssState) {
        currentState[urlToCssFile] = {
          instances: existingCssState.instances + 1,
          element: existingCssState.element,
        };

        if (onLoad) {
          // The callback probably does not expect to be called synchronously.
          window.setInstrumentedTimeout(() => onLoad(), 0);
        }

        return;
      }

      // Otherwise, create a new element and new state.
      const {document} = window;
      const element = document.createElement('link');
      element.rel = 'stylesheet';
      element.href = urlToCssFile;

      if (onLoad) {
        element.addEventListener('load', () => onLoad(), {once: true});
      }

      document.head.appendChild(element);

      currentState[urlToCssFile] = {
        instances: 1,
        element,
      };
    },
    [window],
  );

  const unregisterCss = useCallback((css: string) => {
    const currentState = stateRef.current;
    const cssState = currentState[css];
    if (!cssState) {
      throw new Error('A state could not be found for this CSS.');
    }

    // If there are more instances, just decrement.
    const {instances, element} = cssState;
    if (instances > 1) {
      currentState[css] = {
        instances: instances - 1,
        element,
      };
      return;
    }

    // Otherwise, remove the element and remove the state.
    element.remove();
    delete currentState[css];
  }, []);

  const addCssToDocument = useCallback(
    (css: string, onLoad?: () => void) => {
      registerCss(css, onLoad);
      return () => {
        unregisterCss(css);
      };
    },
    [registerCss, unregisterCss],
  );

  /*
   * Render.
   */

  const context = useMemo(() => ({addCssToDocument}), [addCssToDocument]);
  return <CssContextProvider value={context}>{children}</CssContextProvider>;
};
